/**
 * Media Buyer Command Center (Task 207).
 *
 * The assigned-client paid-media monitoring surface for an agency
 * `media_buyer` with MULTIPLE assigned clients. Rendered by `AppLanding` ONLY
 * when `isMediaBuyer(me)` is true, `me.client_assignments.length > 1`, and the
 * owner / marketing_manager / account_manager gates did not fire first. A
 * single-client media_buyer is bounced to that client (unchanged); a
 * zero-client one keeps the Task 200 scoped empty state. Display-only and
 * gated upstream.
 *
 * SCOPE: ASSIGNED clients only — reads `me.client_assignments`, deliberately
 * NOT `useClientScope` (which exposes the whole org for leadership). No
 * agency-wide clients, no agency revenue/profit, no owner/MM/AM content, no
 * owner-management controls.
 *
 * READ-ONLY: media_buyer holds only paid-media READ permissions. Every write
 * (budget change, pause) and reconnect/OAuth action is DISABLED («قريباً») or
 * framed as an escalation to the Marketing Manager — never a direct mutation.
 * TikTok + Google stay «قريباً» (no fabricated active state); Meta + Snapchat
 * render as «قيد الربط» until a live feed exists.
 *
 * Design reference: design_handoff_super_agency/screens/26-media-buyer-command-center.html
 * (recreated with the shared `.occ-*` styles via an `occ mbcc` root). RTL.
 * Paid-media metrics with no live endpoint render honest «قيد الربط»
 * placeholders / empty states; the only real data is the assigned-client list
 * + status-derived follow-ups.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { MeContext } from '../api/types';
import {
  PLATFORMS,
  SOON,
  avatarColor,
  buildPaidMediaFollowUps,
  clientStatusLabelAr,
  initial,
  statusBadgeClass,
  statusRisk,
  statusWeight,
  toArabicDigits,
} from './mediaBuyerCommandCenter.logic';

// ── Inline icon set (24×24, currentColor stroke) ─────────────────────
type IconName =
  | 'search'
  | 'plus'
  | 'bell'
  | 'megaphone'
  | 'flame'
  | 'wallet'
  | 'target'
  | 'trend-up'
  | 'plug'
  | 'bolt'
  | 'chev'
  | 'refresh'
  | 'chart'
  | 'arrow-up';

function Icon({ name, className }: { readonly name: IconName; readonly className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      {iconBody(name)}
    </svg>
  );
}

function iconBody(name: IconName): ReactNode {
  switch (name) {
    case 'search':
      return (
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" strokeLinecap="round" />
        </>
      );
    case 'plus':
      return <path d="M12 5v14M5 12h14" strokeLinecap="round" />;
    case 'bell':
      return (
        <path
          d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9M13.7 21a2 2 0 0 1-3.4 0"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'megaphone':
      return (
        <path
          d="M3 11v2a1 1 0 0 0 1 1h3l7 5V5L7 10H4a1 1 0 0 0-1 1zM18 8a5 5 0 0 1 0 8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'flame':
      return (
        <path
          d="M12 2s5 4 5 9a5 5 0 0 1-10 0c0-1.5.6-2.7 1.3-3.6C9 9 9.5 10 10.5 10c0-2 .5-5 1.5-8z"
          strokeLinejoin="round"
        />
      );
    case 'wallet':
      return (
        <path
          d="M3 7a2 2 0 0 1 2-2h12v3M3 7v10a2 2 0 0 0 2 2h14a1 1 0 0 0 1-1v-3M3 7h17a1 1 0 0 1 1 1v3M17 13h.01"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'target':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="5" />
        </>
      );
    case 'trend-up':
      return (
        <path
          d="M3 17l7.5-7.5 4 4L21 7M21 7h-5M21 7v5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'plug':
      return (
        <path
          d="M9 2v6M15 2v6M7 8h10v3a5 5 0 0 1-10 0zM12 16v6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'bolt':
      return <path d="M13 2L3 14h8l-1 8 10-12h-8z" strokeLinejoin="round" />;
    case 'chev':
      return <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />;
    case 'refresh':
      return (
        <path
          d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'chart':
      return <path d="M3 21V5M3 21h18M7 16v-6M12 16v-9M17 16v-3" strokeLinecap="round" />;
    case 'arrow-up':
      return <path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round" />;
    default:
      return null;
  }
}

interface MediaBuyerCommandCenterProps {
  readonly me: MeContext;
}

export function MediaBuyerCommandCenter({ me }: MediaBuyerCommandCenterProps) {
  // ASSIGNED clients only — never the org-wide list.
  const assignments = me.client_assignments;
  const mbName = me.profile?.full_name ?? me.profile?.email ?? 'مدير الإعلانات';

  const followUps = buildPaidMediaFollowUps(assignments);

  const tableRows = [...assignments]
    .sort((a, b) => statusWeight(b.client_status) - statusWeight(a.client_status))
    .slice(0, 8);

  return (
    <div className="occ mbcc" dir="rtl">
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <div className="occ-top">
        <div className="occ-search">
          <input type="search" placeholder="ابحث في حملات عملائك المعيّنين…" aria-label="بحث" />
          <Icon name="search" />
        </div>
        <div className="occ-top-actions">
          <div className="occ-range" role="group" aria-label="النطاق الزمني">
            <button type="button" className="active" aria-pressed="true">
              ٧ أيام
            </button>
            <button type="button">هذا الشهر</button>
            <button type="button">الربع</button>
          </div>
          <Link to="/app/paid-media" className="occ-create">
            <Icon name="plus" />
            إجراء سريع
          </Link>
          <Link
            to="/app/notifications"
            className="occ-iconbtn"
            title="الإشعارات"
            aria-label="الإشعارات"
          >
            <Icon name="bell" />
            <span className="occ-dot" />
          </Link>
        </div>
      </div>

      {/* ── Page header ─────────────────────────────────────────── */}
      <header className="occ-head">
        <h1 className="h-page">
          مركز شراء{' '}
          <em className="serif italic" style={{ color: 'var(--tangerine)' }}>
            الإعلانات
          </em>
        </h1>
        <p className="occ-sub">
          <span className="mbcc-scope">
            عملائي فقط — {toArabicDigits(assignments.length)} عملاء
          </span>{' '}
          أهلاً {mbName}.{' '}
          {followUps.length > 0 ? (
            <>
              <b>{followUps.length} حملة/حساب</b> بحاجة إلى مراجعتك اليوم.
            </>
          ) : (
            'لا توجد بنود عاجلة الآن.'
          )}{' '}
          <span className="occ-note-inline">
            مؤشّرات الأداء المدفوع قيد الربط بالبيانات المباشرة.
          </span>
        </p>
      </header>

      {/* ── Platform filter ─────────────────────────────────────── */}
      <div className="mbcc-pfilter" role="group" aria-label="تصفية حسب المنصّة">
        <button type="button" className="mbcc-pf active" aria-pressed="true">
          كل المنصّات
        </button>
        {PLATFORMS.map((p) =>
          p.filterable ? (
            <button type="button" key={p.key} className="mbcc-pf">
              <span className="mbcc-pdot" style={{ background: p.accent }} />
              {p.name.replace(' Ads', '')}
            </button>
          ) : (
            <button
              type="button"
              key={p.key}
              className="mbcc-pf soon"
              disabled
              title="قريباً — لا توجد بيانات مرتبطة بعد"
            >
              <span className="mbcc-pdot" style={{ background: p.accent }} />
              {p.name.replace(' Ads', '')}
            </button>
          ),
        )}
      </div>

      {/* ── KPI strip (6) — all paid-media metrics pending a live feed ── */}
      <div className="occ-pulse">
        <span className="occ-blob" />
        <Kpi icon="megaphone" cap="الحملات النشطة" value={SOON} note="قيد الربط" />
        <Kpi icon="flame" cap="تحتاج تدخّل" value={SOON} note="قيد الربط" risk />
        <Kpi icon="wallet" cap="الصرف هذا الأسبوع" value={SOON} note="قيد الربط" />
        <Kpi icon="target" cap="متوسط ROAS" value={SOON} note="قيد الربط" />
        <Kpi icon="trend-up" cap="CPA مرتفع" value={SOON} note="قيد الربط" risk />
        <Kpi icon="plug" cap="حسابات غير متزامنة" value={SOON} note="قيد الربط" risk />
      </div>

      {/* ── Primary queue ───────────────────────────────────────── */}
      <section className="occ-decisions">
        <div className="occ-dec-head">
          <span className="occ-dh-ic">
            <Icon name="bolt" />
          </span>
          <span className="occ-dh-t">
            حملات تحتاج <em>إجراءك</em> اليوم
          </span>
          <span className="occ-dh-c">
            {followUps.length > 0 ? `${toArabicDigits(followUps.length)} بنود` : 'لا بنود'}
          </span>
        </div>
        {followUps.length > 0 ? (
          followUps.map((d) => (
            <div key={d.key} className={`occ-dec ${d.tone}`}>
              <span className={`occ-sev ${d.sev}`}>
                <span className="occ-sd" />
                {d.sevLabel}
              </span>
              <span className="occ-d-ic">
                <Icon name={d.icon} />
              </span>
              <div className="occ-d-body">
                <div className="occ-d-t">{d.title}</div>
                <div className="occ-d-why">{d.why}</div>
              </div>
              <Link to={d.to} className="occ-d-act">
                {d.action}
                <Icon name="chev" />
              </Link>
            </div>
          ))
        ) : (
          <div className="occ-empty">
            ستظهر هنا الحملات التي تحتاج تدخّلك — العائد الهابط، الإنفاق الزائد، المزامنة المتأخّرة،
            والحسابات المنقطعة — مرتّبة حسب الأولوية، فور ربط مؤشّرات الأداء المدفوع المباشرة.
          </div>
        )}
      </section>

      {/* ── Platform / account health ───────────────────────────── */}
      <div className="occ-strip-label">
        <span className="lead">صحة الحسابات الإعلانية</span>
      </div>
      <div className="mbcc-phealth">
        {PLATFORMS.map((p) => (
          <div key={p.key} className="mbcc-phc">
            <div className="mbcc-phc-top">
              <span className="mbcc-pi" style={{ background: p.accent }}>
                {p.abbr}
              </span>
              <span className="mbcc-pnm">{p.name}</span>
              <span className={`mbcc-pstate ${p.state === 'soon' ? 'ps-soon' : 'ps-pending'}`}>
                {p.stateLabel}
              </span>
            </div>
            <div className="mbcc-psync">{p.sync}</div>
            <button type="button" className="mbcc-p-act" disabled title="قيد الربط">
              {p.state !== 'soon' && <Icon name="refresh" />}
              {p.action}
            </button>
          </div>
        ))}
      </div>

      {/* ── Main grid ───────────────────────────────────────────── */}
      <div className="occ-grid">
        <div>
          {/* Campaign exceptions */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                استثناءات <em>الحملات</em>
              </div>
              <Link to="/app/paid-media" className="occ-card-link">
                كل الحملات <Icon name="chev" />
              </Link>
            </div>
            <div className="occ-empty">
              تعرض هذه البطاقة <b>الاستثناءات فقط</b> — العائد المنخفض، الإنفاق الزائد، CPA المرتفع،
              والمزامنة المتأخّرة — وليست لوحة حملات كاملة. ستظهر فور ربط بيانات الأداء المدفوع لـ
              Meta و Snapchat.{' '}
              <Link to="/app/paid-media" className="occ-inline-link">
                افتح الإعلانات المدفوعة ←
              </Link>
            </div>
          </section>

          {/* Budget pacing */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                وتيرة <em>الميزانية</em>
              </div>
            </div>
            <div className="occ-empty">
              سيظهر هنا تتبّع وتيرة الصرف مقابل الميزانية لكل حملة — الصرف السريع/البطيء والتجاوز
              المتوقّع — فور ربط بيانات الميزانية. تعديل الميزانية نفسه يتم عبر التصعيد لمدير
              التسويق.
            </div>
          </section>
        </div>

        <div>
          {/* Performance trend */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                اتجاه <em>الأداء</em>
              </div>
            </div>
            <div className="occ-empty sm">
              سيظهر هنا اتجاه الصرف و ROAS و CPA لآخر ٧ أيام فور ربط بيانات الأداء المباشرة.
            </div>
          </section>

          {/* Assigned clients paid-media health */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                إعلانات <em>عملائي</em>
              </div>
              <span className="occ-card-link" aria-hidden="true">
                {toArabicDigits(assignments.length)} عملاء
              </span>
            </div>
            {tableRows.length > 0 ? (
              <table className="occ-ctable">
                <thead>
                  <tr>
                    <th>العميل</th>
                    <th className="c">المنصّات</th>
                    <th className="c">الأداء</th>
                    <th className="c">الخطر</th>
                    <th>الإجراء</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((a) => {
                    const risk = statusRisk(a.client_status);
                    return (
                      <tr key={a.client_id}>
                        <td>
                          <div className="occ-cl-cell">
                            <span
                              className="occ-cl-av"
                              style={{ background: avatarColor(a.client_id) }}
                            >
                              {initial(a.client_name)}
                            </span>
                            <span>
                              <span className="occ-cl-nm">{a.client_name}</span>
                              <span className="occ-cl-sub">
                                <span className={`occ-badge ${statusBadgeClass(a.client_status)}`}>
                                  {clientStatusLabelAr(a.client_status)}
                                </span>
                              </span>
                            </span>
                          </div>
                        </td>
                        <td className="c occ-muted">{SOON}</td>
                        <td className="c occ-muted">{SOON}</td>
                        <td className="c">
                          <span className={`occ-risk ${risk.cls}`}>
                            <span className="rd" />
                            {risk.label}
                          </span>
                        </td>
                        <td>
                          <Link to={`/app/clients/${a.client_id}/paid-media`} className="occ-next">
                            فتح الإعلانات <Icon name="chev" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="occ-empty">لم تُسنَد إليك أي عملاء حتى الآن.</div>
            )}
          </section>

          {/* Escalations to Marketing Manager */}
          <section className="occ-card">
            <div className="occ-card-head">
              <div className="occ-card-title">
                تصعيد <em>للتسويق</em>
              </div>
            </div>
            <div className="occ-empty sm">
              ستظهر هنا البنود التي تتجاوز صلاحياتك — طلبات رفع الميزانية، إيقاف الحملات المتعثّرة،
              وصلاحيات الوصول المنتهية — للتصعيد إلى مدير التسويق. التعديلات على الميزانية والإيقاف
              لا تتم مباشرة من هنا.
            </div>
          </section>

          {/* Quick actions */}
          <div className="occ-qa-card">
            <div className="occ-qa-label">إجراءات سريعة</div>
            <div className="occ-qa-row">
              <Link to="/app/paid-media" className="occ-qa">
                <Icon name="megaphone" /> مراجعة حملة
              </Link>
              <Link to="/app/reports" className="occ-qa">
                <Icon name="chart" /> تقارير الأداء
              </Link>
              <button type="button" className="occ-qa" disabled title="قيد الربط">
                <Icon name="refresh" /> طلب إعادة ربط
              </button>
              <button type="button" className="occ-qa" disabled title="قيد الربط">
                <Icon name="arrow-up" /> تصعيد للتسويق
              </button>
              <button type="button" className="occ-qa" disabled title="قريباً — غير مدعوم">
                <Icon name="wallet" /> ضبط الميزانية
              </button>
              <button type="button" className="occ-qa" disabled title="قريباً — غير مدعوم">
                <Icon name="target" /> إيقاف حملة
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({
  icon,
  cap,
  value,
  note,
  risk,
}: {
  readonly icon: IconName;
  readonly cap: string;
  readonly value: string;
  readonly note: string;
  readonly risk?: boolean;
}) {
  return (
    <div className="occ-pl">
      <div className="occ-cap">
        <Icon name={icon} />
        {cap}
      </div>
      <div className={`occ-big${risk ? ' risk' : ''}`}>{value}</div>
      <div className="occ-delta">{note}</div>
    </div>
  );
}
